import React, { useEffect, useRef, useState } from 'react';
import * as faceapi from 'face-api.js';
import { Box, Typography, Button, CircularProgress, Avatar } from '@mui/material';

const FaceRecognition = ({ examDetails, onVerificationSuccess }) => {
  const videoRef = useRef();
  const canvasRef = useRef();

  const [loading, setLoading] = useState(true);
  const [examCardPhoto, setExamCardPhoto] = useState(null);
  const [status, setStatus] = useState('Initializing...');
  const [matchFound, setMatchFound] = useState(false);

  const token = localStorage.getItem('token');

  // Load face-api models including SsdMobilenetv1
  const loadModels = async () => {
    const MODEL_URL = '/models';
    await Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL), // ✅ ADDED
    ]);
  };

  // Start webcam
  const startVideo = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: {} });
      videoRef.current.srcObject = stream;
    } catch (err) {
      console.error('Camera access denied:', err);
      setStatus('Camera access denied');
    }
  };

  // Fetch iCard photo for the course
  const fetchExamCard = async () => {
    try {
      const res = await fetch(`http://196.216.9.34:5020/api/icards/course/${encodeURIComponent(examDetails.courseTitle)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('No iCard found for this course');

      const data = await res.json();
      setExamCardPhoto(`http://196.216.9.34:5020${data.profile_image_url}`);
    } catch (err) {
      console.error('Error fetching exam card:', err);
      setStatus('Could not load iCard for this course');
    }
  };

  // Perform face comparison
  const handleRecognition = async () => {
    if (!examCardPhoto || !videoRef.current) return;

    setStatus('Detecting face in iCard photo...');
    const img = await faceapi.fetchImage(examCardPhoto);
    const labeledDescriptor = await faceapi
      .detectSingleFace(img, new faceapi.SsdMobilenetv1Options()) // ✅ USE SSD FOR STATIC IMAGE
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (!labeledDescriptor) {
      setStatus('No face detected in iCard photo');
      return;
    }

    const faceMatcher = new faceapi.FaceMatcher(labeledDescriptor, 0.6);

    setStatus('Matching live video face...');

    const interval = setInterval(async () => {
      const detection = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (detection) {
        const bestMatch = faceMatcher.findBestMatch(detection.descriptor);
        if (bestMatch.label !== 'unknown') {
          clearInterval(interval);
          setMatchFound(true);
          setStatus('✅ Face matched! Access granted.');
          onVerificationSuccess(); // Notify parent
        } else {
          setStatus('⚠️ Face not recognized. Try again...');
        }
      } else {
        setStatus('No face detected in live feed...');
      }
    }, 2000);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadModels();
      await fetchExamCard();
      await startVideo();
      setLoading(false);
    };

    if (examDetails?.courseTitle) {
      init();
    }
  }, [examDetails?.courseTitle]);

  useEffect(() => {
    if (!loading && examCardPhoto) {
      handleRecognition();
    }
  }, [loading, examCardPhoto]);

  return (
    <Box textAlign="center" mt={5}>
      <Typography variant="h5" gutterBottom>
        Face Verification for {examDetails.courseTitle}
      </Typography>

      {examCardPhoto && (
        <Box mb={3}>
          <Typography variant="body1">Exam iCard Photo:</Typography>
          <Avatar
            src={examCardPhoto}
            alt="Exam Card"
            sx={{ width: 100, height: 100, mx: 'auto', mt: 1, border: '2px solid #ff6600' }}
          />
        </Box>
      )}

      <Box display="flex" justifyContent="center" mb={2}>
        <video ref={videoRef} autoPlay muted width="360" height="280" style={{ borderRadius: 8 }} />
      </Box>

      <Typography variant="body1" color={matchFound ? 'green' : 'orange'}>
        {status}
      </Typography>

      {loading && <CircularProgress sx={{ mt: 2 }} />}
    </Box>
  );
};

export default FaceRecognition;
